import React, { useContext, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Grid, LinearProgress, TextField, Typography } from '@material-ui/core';
import { CheckCircleOutline } from '@material-ui/icons';
import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { UserContext } from '@/components/App';
import NavResponsiveModal, { ModalHeader } from '@/components/Navigation/NavResponsiveModal';
import FileDropZone from '@/components/OrderDetailsPage/FileDropZone';
import { ModalContent, PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { greys, reds } from '@/styles/colors';
import uploadCorpDocumentAsync from '@/utilities/uploadCorpDocumentAsync';

import { ARCHIVE_CORP_DOCUMENT, INSERT_CORP_DOCUMENT } from '../graphql';
import { CorpAdminContext } from '../store';
import { evaluateDocStatus } from './DocStatusIcon';

const DOC_TYPE_LABELS = {
    AGREEMENT: 'Agreement',
    COI: 'Certificate of Insurance',
    W9: 'W9',
    RATES: 'Rate Addendum',
};

const TYPES_REQUIRING_EXPIRATION = new Set(['COI']);

const DocumentUploadModal = ({
    open,
    onClose,
    type,
    client_id,
    partner_client_id = null,
    existingDoc,
    contextLabel,
}) => {
    const { user } = useContext(UserContext);
    const { callbacks } = useContext(CorpAdminContext);
    const { setNotification, refetchCorpDocs } = callbacks;

    const [name, setName] = useState('');
    const [link, setLink] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (open) {
            setName('');
            setLink('');
            setExpirationDate('');
        }
    }, [open]);

    const [archiveDocument] = useMutation(ARCHIVE_CORP_DOCUMENT);
    const [insertDocument, { loading: inserting }] = useMutation(INSERT_CORP_DOCUMENT);

    const handleFileAdd = async (file, fileType) => {
        if (!file) return;
        setUploading(true);
        try {
            const fileUrl = URL.createObjectURL(file);
            const downloadUrl = await uploadCorpDocumentAsync(fileUrl, fileType);
            setLink(downloadUrl);
            if (file.name) setName(file.name);
        } catch (err) {
            Sentry.captureException(err);
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        try {
            if (existingDoc?.document_id) {
                await archiveDocument({ variables: { document_id: existingDoc.document_id } });
            }

            await insertDocument({
                variables: {
                    object: {
                        client_id,
                        partner_client_id,
                        organization_id: user?.organization_id || null,
                        type,
                        link,
                        name,
                        expiration_date: expirationDate || null,
                        archived: false,
                    },
                },
            });

            if (refetchCorpDocs) await refetchCorpDocs();
            setNotification?.({ severity: 'success', message: 'Document uploaded' });
            onClose();
        } catch (err) {
            Sentry.captureException(err);
            console.error(err);
            setNotification?.({ severity: 'error', message: 'Error uploading document' });
        }
    };

    const expirationRequired = TYPES_REQUIRING_EXPIRATION.has(type);
    const valid = !!link && !!name && (!expirationRequired || !!expirationDate);
    const saving = inserting;
    const headerLabel = `${DOC_TYPE_LABELS[type] || type}${contextLabel ? ` — ${contextLabel}` : ''}`;

    return (
        <NavResponsiveModal open={open} onClose={onClose} maxWidth="md" fullWidth>
            <ModalHeader
                title={existingDoc ? `Replace ${headerLabel}` : `Upload ${headerLabel}`}
                onClose={onClose}
                css={css`
                    border-bottom: 1px solid lightgray;
                `}
            />
            {(uploading || saving) && (
                <LinearProgress
                    color="primary"
                    css={css`
                        width: 100%;
                    `}
                />
            )}
            <ModalContent
                css={css`
                    width: 100%;
                `}
            >
                <Grid container direction="column">
                    {existingDoc && (
                        <Grid
                            item
                            css={css`
                                margin-bottom: 16px;
                                padding: 12px 16px;
                                border: 1px solid #e2e2e2;
                                border-radius: 4px;
                                background: #fafafa;
                            `}
                        >
                            <Typography
                                css={css`
                                    font-size: 11px;
                                    text-transform: uppercase;
                                    letter-spacing: 0.04em;
                                    color: ${greys[2]};
                                    margin-bottom: 6px;
                                `}
                            >
                                Current file
                            </Typography>
                            <div
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    justify-content: space-between;
                                    gap: 1rem;
                                `}
                            >
                                <div
                                    css={css`
                                        min-width: 0;
                                    `}
                                >
                                    <Typography
                                        css={css`
                                            font-weight: 600;
                                        `}
                                    >
                                        {existingDoc.name}
                                    </Typography>
                                    <Typography
                                        css={css`
                                            font-size: 12px;
                                            color: ${greys[2]};
                                        `}
                                    >
                                        {existingDoc.expiration_date
                                            ? `Expires ${existingDoc.expiration_date}`
                                            : 'No expiration set'}
                                        {(() => {
                                            const s = evaluateDocStatus(existingDoc).state;
                                            if (s === 'expired') {
                                                return (
                                                    <strong
                                                        css={css`
                                                            color: ${reds.primary};
                                                            margin-left: 0.5rem;
                                                        `}
                                                    >
                                                        Expired
                                                    </strong>
                                                );
                                            }
                                            if (s === 'expiring') {
                                                return (
                                                    <strong
                                                        css={css`
                                                            color: #b16800;
                                                            margin-left: 0.5rem;
                                                        `}
                                                    >
                                                        Expiring soon
                                                    </strong>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </Typography>
                                </div>
                                {existingDoc.link && (
                                    <SecondaryButton
                                        as="a"
                                        href={existingDoc.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View
                                    </SecondaryButton>
                                )}
                            </div>
                            <Typography
                                css={css`
                                    font-size: 12px;
                                    color: ${greys[2]};
                                    margin-top: 8px;
                                    font-style: italic;
                                `}
                            >
                                Uploading a new file will archive this one.
                            </Typography>
                        </Grid>
                    )}

                    {link ? (
                        <Grid
                            item
                            css={css`
                                height: 100px;
                                width: 100%;
                                align-content: center;
                                text-align: center;
                            `}
                        >
                            File Selected&nbsp;
                            <CheckCircleOutline />
                        </Grid>
                    ) : (
                        <FileDropZone
                            customLabel={' '}
                            customStyle={{
                                height: '100px',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: 'white',
                                borderRadius: '5px',
                                outline: '1px solid #c4c4c4',
                                fontFamily: 'Montserrat',
                                cursor: 'pointer',
                            }}
                            handleFileAdd={handleFileAdd}
                            disabled={uploading}
                        />
                    )}

                    <Grid
                        container
                        direction="row"
                        spacing={2}
                        css={css`
                            margin-top: 8px;
                        `}
                    >
                        <Grid item xs={expirationRequired ? 6 : 12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Grid>
                        {expirationRequired && (
                            <Grid item xs={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    type="date"
                                    label="Expiration Date"
                                    required
                                    error={!expirationDate}
                                    helperText={!expirationDate ? 'Expiration date is required' : undefined}
                                    InputLabelProps={{ shrink: true }}
                                    value={expirationDate}
                                    onChange={(e) => setExpirationDate(e.target.value)}
                                />
                            </Grid>
                        )}
                    </Grid>

                    <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        css={css`
                            margin-top: 24px;
                            width: 100%;
                        `}
                    >
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <PrimaryButton disabled={!valid || saving || uploading} onClick={handleSave}>
                            Save
                        </PrimaryButton>
                    </Grid>
                </Grid>
            </ModalContent>
        </NavResponsiveModal>
    );
};

export default DocumentUploadModal;
